

// src/app/modules/Members/pages/MemberDetails.jsx
// Complete file — all modals working, spaces loaded, buttons styled

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import * as actions from '../../../Dashbord/_redux/dashboard/Actions';
import { Tabs, Tab } from 'react-bootstrap';
import { ConfirmActionModal } from '../../../../components/ConfirmActionModal';
import { MemberDetailsSkeleton } from '../../../../../_metronic/layout/components/skeleton/SkeletonLoader';
import {
  ArrowLeft, User, Mail, Phone, Shield, Calendar, Clock,
  UserX, UserCheck, Trash2, MapPin, Briefcase, TrendingUp, Settings2,
  Activity, Clock3, Coffee, Award, AlertCircle, CheckCircle,
  Timer, BarChart3, ChevronLeft, ChevronRight,
  StickyNote, DoorOpen, Sunrise, Sunset, X,
} from 'lucide-react';
import Moment from 'moment';
import './member-details.scss';
import '../../../../../_metronic/layout/components/skeleton/detail-tabs.scss';
import { useOrgConfig } from "../../../../../hooks/useOrgConfig";

// ─── Period helpers ────────────────────────────────────────────────────────────

const buildPeriod = (monthOffset = 0) => {
  const anchor = Moment().add(monthOffset, 'months');
  return {
    from_date:      anchor.clone().startOf('month').toISOString(),
    to_date:        anchor.clone().endOf('month').toISOString(),
    label:          anchor.format('MMMM YYYY'),
    isCurrentMonth: monthOffset === 0,
  };
};

// ─── Sub-components ────────────────────────────────────────────────────────────

const TabLabel = ({ icon: Icon, label, count }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <Icon size={13} />
    {label}
    {count != null && <span className="tab-count">{count}</span>}
  </span>
);

const PeriodNav = ({ period, onPrev, onNext }) => (
  <div className="smd-period-nav">
    <button className="smd-period-btn" onClick={onPrev}><ChevronLeft size={15} /></button>
    <span className="smd-period-label"><Calendar size={13} />{period.label}</span>
    <button className="smd-period-btn" onClick={onNext} disabled={period.isCurrentMonth}>
      <ChevronRight size={15} />
    </button>
  </div>
);

const ShiftRow = ({ shift }) => {
  const [expanded, setExpanded] = useState(false);
  const isOngoing = !shift.exit_time;
  const isOnBreak  = shift.status === 'on_break';

  // local_times contains pre-formatted strings ("07:48 AM") — never parse with Moment
  // Use entry_time (ISO) for date portions, local_times for time display strings
  const entryTimeStr    = shift.local_times?.entry            || Moment(shift.entry_time).format('h:mm A');
  const exitTimeStr     = shift.local_times?.exit             || (shift.exit_time ? Moment(shift.exit_time).format('h:mm A') : null);
  const schedInStr      = shift.local_times?.scheduled_start  || (shift.scheduled_shift_start ? Moment(shift.scheduled_shift_start).format('h:mm A') : null);
  const schedOutStr     = shift.local_times?.scheduled_end    || (shift.scheduled_shift_end   ? Moment(shift.scheduled_shift_end).format('h:mm A')   : null);
  const tzLabel = shift.timezone || 'UTC';

  return (
    <div className={`smd-shift-item ${expanded ? 'smd-shift-item--expanded' : ''} ${isOngoing ? 'smd-shift-item--ongoing' : ''}`}>
      <div className="smd-shift-main" onClick={() => setExpanded(e => !e)}>
        <div className="shift-cal">
          <div className="shift-cal__day">{Moment(shift.entry_time).format('DD')}</div>
          <div className="shift-cal__month">{Moment(shift.entry_time).format('MMM')}</div>
        </div>
        <div className="shift-meta">
          <div className="shift-meta__time">
            <Clock3 size={12} />
            {entryTimeStr} —{' '}
            {isOngoing
              ? <span className={`shift-ongoing-pill ${isOnBreak ? 'shift-ongoing-pill--break' : ''}`}>
                  {isOnBreak ? '☕ On Break' : '● Live'}
                </span>
              : (exitTimeStr || '—')}
            <span className="shift-tz-label">{tzLabel}</span>
          </div>
          <div className="shift-meta__sub">
            {shift.worked_hours ? `${shift.worked_hours.toFixed(1)}h worked` : isOngoing ? 'In progress' : '—'}
            {shift.overtime_hours > 0 && <span className="shift-ot-chip">+{shift.overtime_hours.toFixed(1)}h OT</span>}
            {shift.break_logs?.length > 0 && <span className="shift-break-chip"><Coffee size={9} />{shift.break_logs.length} break{shift.break_logs.length > 1 ? 's' : ''}</span>}
            {shift.entry_point && shift.entry_point !== 'Main Entrance' && <span className="shift-entry-chip"><DoorOpen size={9} />{shift.entry_point}</span>}
          </div>
        </div>
        <div className="shift-right">
          {shift.shift_status && (
            <span className={`smd-shift-badge smd-shift-badge--${shift.shift_status}`}>
              {shift.shift_status === 'on_time' && <CheckCircle size={11} />}
              {shift.shift_status === 'late'    && <AlertCircle size={11} />}
              {shift.shift_status === 'early'   && <Clock size={11} />}
              {shift.shift_status?.replace('_', ' ')}
              {shift.shift_status === 'late'  && shift.minutes_late  > 0 && ` · ${shift.minutes_late}m`}
              {shift.shift_status === 'early' && shift.minutes_early > 0 && ` · ${shift.minutes_early}m`}
            </span>
          )}
          {(shift.break_logs?.length > 0 || shift.notes) && (
            <span className={`shift-expand-hint ${expanded ? 'shift-expand-hint--open' : ''}`}><ChevronRight size={12} /></span>
          )}
        </div>
      </div>

      {expanded && (
        <div className="smd-shift-drawer">
          {shift.break_logs?.length > 0 && (
            <div className="shift-drawer-section">
              <div className="shift-drawer-title"><Coffee size={12} /> Breaks</div>
              <div className="shift-break-list">
                {shift.break_logs.map((b, i) => (
                  <div key={i} className="shift-break-row">
                    <span className="shift-break-type">{b.break_type || 'rest'}</span>
                    <span className="shift-break-time">
                      {Moment(b.break_start).format('h:mm A')}
                      {b.break_end ? ` — ${Moment(b.break_end).format('h:mm A')}` : ' (ongoing)'}
                    </span>
                    {b.break_duration_minutes > 0 && <span className="shift-break-dur">{b.break_duration_minutes}m</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {(shift.location || shift.checkout_location) && (
            <div className="shift-drawer-section">
              <div className="shift-drawer-title"><MapPin size={12} /> Location</div>
              <div className="shift-location-pair">
                {shift.location && (
                  <div className="shift-loc-row shift-loc-row--in">
                    <div className="shift-loc-icon"><MapPin size={13} /></div>
                    <div className="shift-loc-body">
                      <div className="shift-loc-label">Checked in</div>
                      <div className="shift-loc-name">{shift?.metadata?.checkin_location_name || `${shift.location.latitude?.toFixed(4)}, ${shift.location.longitude?.toFixed(4)}`}</div>
                    </div>
                    <div className="shift-loc-time">{entryTimeStr}</div>
                  </div>
                )}
                {shift.checkout_location && (
                  <div className="shift-loc-row shift-loc-row--out">
                    <div className="shift-loc-icon"><MapPin size={13} /></div>
                    <div className="shift-loc-body">
                      <div className="shift-loc-label">Checked out</div>
                      <div className="shift-loc-name">{shift?.metadata?.checkout_location_name || `${shift.checkout_location.latitude?.toFixed(4)}, ${shift.checkout_location.longitude?.toFixed(4)}`}</div>
                    </div>
                    <div className="shift-loc-time">{exitTimeStr || '—'}</div>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="shift-drawer-section">
            <div className="shift-drawer-title">
              <Timer size={12} /> Timings · <span style={{ fontWeight: 500, opacity: 0.75 }}>{tzLabel}</span>
            </div>
            <div className="shift-timing-grid">
              {schedInStr && (
                <>
                  <div className="shift-timing-item"><Sunrise size={11} /><span>Scheduled In</span><strong>{schedInStr}</strong></div>
                  <div className="shift-timing-item"><Sunset size={11} /><span>Scheduled Out</span><strong>{schedOutStr || '—'}</strong></div>
                </>
              )}
              {shift.total_break_minutes > 0 && <div className="shift-timing-item"><Coffee size={11} /><span>Total Break</span><strong>{shift.total_break_minutes}m</strong></div>}
              {shift.overtime_hours > 0 && <div className="shift-timing-item"><Timer size={11} /><span>Overtime</span><strong className="shift-timing-item--ot">{shift.overtime_hours.toFixed(1)}h</strong></div>}
            </div>
          </div>
          {shift.notes && (
            <div className="shift-drawer-section">
              <div className="shift-drawer-title"><StickyNote size={12} /> Notes</div>
              <div className="shift-notes">{shift.notes}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AttendanceSummary = ({ shifts }) => {
  if (!shifts.length) return null;
  const completed  = shifts.filter(s => s.status === 'checked_out');
  const totalHours = completed.reduce((s, x) => s + (x.worked_hours || 0), 0);
  const totalOT    = completed.reduce((s, x) => s + (x.overtime_hours || 0), 0);
  const lateCount  = completed.filter(s => s.shift_status === 'late').length;
  const onTimeRate = completed.length ? Math.round((completed.filter(s => s.shift_status !== 'late').length / completed.length) * 100) : 0;
  return (
    <div className="smd-attendance-summary">
      <div className="att-sum-item"><div className="att-sum-val">{totalHours.toFixed(1)}h</div><div className="att-sum-lbl">Hours</div></div>
      <div className="att-sum-item"><div className="att-sum-val">{completed.length}</div><div className="att-sum-lbl">Shifts</div></div>
      <div className="att-sum-item"><div className={`att-sum-val ${onTimeRate < 70 ? 'att-sum-val--warn' : 'att-sum-val--good'}`}>{onTimeRate}%</div><div className="att-sum-lbl">On-Time</div></div>
      <div className="att-sum-item"><div className={`att-sum-val ${lateCount > 3 ? 'att-sum-val--danger' : ''}`}>{lateCount}</div><div className="att-sum-lbl">Late</div></div>
      {totalOT > 0 && <div className="att-sum-item"><div className="att-sum-val att-sum-val--ot">{totalOT.toFixed(1)}h</div><div className="att-sum-lbl">OT</div></div>}
    </div>
  );
};

// ─── Modal wrapper ─────────────────────────────────────────────────────────────

const Modal = ({ show, onClose, title, icon: Icon, children, footer }) => {
  if (!show) return null;
  return (
    <div className="smd-modal-overlay" onClick={onClose}>
      <div className="smd-modal" onClick={e => e.stopPropagation()}>
        <div className="smd-modal-header">
          <h3>{Icon && <Icon size={15} />}{title}</h3>
          <button className="smd-modal-close" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="smd-modal-body">{children}</div>
        {footer && <div className="smd-modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

const MEMBER_ROLES = [
  { value: 'admin',    label: 'Admin',    hint: 'Full access, including billing, settings and devices.' },
  { value: 'manager',  label: 'Manager',  hint: 'Manages members, access codes, operations and devices.' },
  { value: 'security', label: 'Security', hint: 'Validates visitors and views logs and devices (read-only).' },
  { value: 'staff',    label: 'Staff',    hint: 'Clocks in and out; no dashboard management.' },
  { value: 'member',   label: 'Member',   hint: 'Basic access (e.g. residents).' },
];

// ─── Main Component ────────────────────────────────────────────────────────────

export function MemberDetails() {
  const dispatch     = useDispatch();
  const navigate     = useNavigate();
  const { id }       = useParams();
  const mountedRef   = useRef(true);
  const { getLabel, org, isBusinessMode } = useOrgConfig();

  // Data
  const [loading,       setLoading]       = useState(false);
  const [memberData,    setMemberData]     = useState(null);
  const [staffStats,    setStaffStats]     = useState(null);
  const [statsLoading,  setStatsLoading]   = useState(false);
  const [shifts,        setShifts]         = useState([]);
  const [shiftsLoading, setShiftsLoading]  = useState(false);
  const [shiftsPag,     setShiftsPag]      = useState({ total: 0, pages: 1, page: 1 });
  const [spaces,        setSpaces]         = useState([]);

  // UI
  const [activeTab,     setActiveTab]      = useState('overview');
  const [monthOffset,   setMonthOffset]    = useState(0);
  const period = buildPeriod(monthOffset);

  // Confirm modals
  const [showSuspend,    setShowSuspend]    = useState(false);
  const [showReactivate, setShowReactivate] = useState(false);
  const [showDelete,     setShowDelete]     = useState(false);
  const [actionLoading,  setActionLoading]  = useState(false);

  const [showManageModal, setShowManageModal] = useState(false);

  // Management modals
  const [showSupervisor, setShowSupervisor] = useState(false);
  const [showSpace,      setShowSpace]      = useState(false);
  const [showLocation,   setShowLocation]   = useState(false);
  const [showRole,       setShowRole]       = useState(false);
  const [roleValue,      setRoleValue]      = useState('');

  // Forms — initialised when modal opens, not on mount
  const [supervisorForm, setSupervisorForm] = useState({ is_supervisor: false, managed_location_ids: [] });
  const [spaceForm,      setSpaceForm]      = useState({ primary_space_id: '', additional_space_ids: [], space_name: '' });
  const [locationForm,   setLocationForm]   = useState({ location_id: '', space_id: '', space_name: '', unit_number: '' });

  const STAFF_ROLES = [
    { group: 'Executive',   options: ['CEO', 'CFO', 'COO', 'CTO'] },
    { group: 'Management',  options: ['General Manager', 'Operations Manager', 'HR Manager', 'Finance Manager', 'Project Manager', 'Team Lead'] },
    { group: 'Security',    options: ['Security Lead', 'Security Officer', 'CCTV Operator', 'Guard'] },
    { group: 'Operations',  options: ['Front Desk', 'Receptionist', 'Admin Staff', 'Customer Service', 'Logistics'] },
    { group: 'Field',       options: ['Driver', 'Technician', 'Cleaner', 'Maintenance', 'Dispatch'] },
    { group: 'Other',       options: ['General Staff', 'Intern', 'Contractor', 'Other'] },
  ];
  const ALL_KNOWN_ROLES = STAFF_ROLES.flatMap(g => g.options);
  const [customRole, setCustomRole] = useState('');
 


  const [manageForm, setManageForm] = useState({
    role: '',
    position: '',
    is_supervisor: false,
    primary_space_id: '',
    additional_space_ids: [],
    primary_location_id: '',
    additional_location_ids: [],
    unit_number: '',
  });

  // Cleanup & Hydration Effect
  useEffect(() => {
    let isSubscribed = true;

    if (showManageModal && memberData && isSubscribed) {
      // console.log(memberData);
      // Crucial: Use optional chaining and default to empty arrays/strings to prevent map() crashes
      // setManageForm({
      //   // role: memberData.staff_role || 'staff',
      //   position: memberData.staff_role || '',
      //   is_supervisor: !!memberData.is_supervisor,
      //   primary_space_id: memberData.primary_space_id || memberData.space_id || '',
      //   additional_space_ids: Array.isArray(memberData.additional_space_ids) ? memberData.additional_space_ids : [],
      //   primary_location_id: memberData.location_id || '',
      //   additional_location_ids: Array.isArray(memberData.additional_location_ids) ? memberData.additional_location_ids : [],
      //   unit_number: memberData.unit_number || '',
      // });

      const savedRole = memberData.staff_role || '';
      const isKnown   = ALL_KNOWN_ROLES.includes(savedRole) || savedRole === '';
      setCustomRole(isKnown ? '' : savedRole);
      setManageForm({
        position:               isKnown ? savedRole : 'Other',
        is_supervisor:          !!memberData.is_supervisor,
        primary_space_id:       memberData.primary_space_id || memberData.space_id || '',
        additional_space_ids:   Array.isArray(memberData.additional_space_ids)  ? memberData.additional_space_ids  : [],
        primary_location_id:    memberData.location_id || memberData.primary_location_id || '',
        additional_location_ids: Array.isArray(memberData.additional_location_ids) ? memberData.additional_location_ids : [],
        unit_number:            memberData.unit_number || '',
      });

    }

    return () => { isSubscribed = false; };
  }, [showManageModal, memberData]);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    mountedRef.current = true;
    if (id) loadAll();
    return () => { mountedRef.current = false; };
  }, [id]);

  useEffect(() => {
    if (memberData && org) {
      fetchStaffStats(memberData.user_id);
      fetchShifts(memberData.user_id, 1);
    }
  }, [memberData, org, monthOffset]);

  // Load spaces once org is known (for modals)
  useEffect(() => {
    if (org?.organization_id) {
      dispatch(actions.fetchAllData('spaces', org.organization_id))
        .then(res => { if (mountedRef.current) setSpaces(res?.results || res?.data || []); })
        .catch(() => {});
    }
  }, [org?.organization_id]);

  async function loadAll() {
    mountedRef.current && setLoading(true);
    try {
      const data = await dispatch(actions.fetchAllData('member', id));
      if (mountedRef.current) setMemberData(data);
    } catch (e) { console.error(e); }
    finally { mountedRef.current && setLoading(false); }
  }

  async function fetchStaffStats(user_id) {
    if (!mountedRef.current) return;
    setStatsLoading(true);
    try {
      const res = await dispatch(actions.fetchAllData('weekly_stats', {
        staff_id: user_id, organization_id: org?.organization_id,
        period: 'month', from_date: period.from_date, to_date: period.to_date,
      }));
      if (mountedRef.current) setStaffStats(res?.data);
    } catch (e) { console.error(e); }
    finally { mountedRef.current && setStatsLoading(false); }
  }

  const fetchShifts = useCallback(async (user_id, page = 1) => {
    if (!mountedRef.current) return;
    setShiftsLoading(true);
    try {
      const res = await dispatch(actions.fetchAllData('shift_history', {
        staff_id: user_id, organization_id: org?.organization_id,
        from_date: period.from_date, to_date: period.to_date, limit: 20, page,
      }));
      if (mountedRef.current) {
        setShifts(res?.data?.shifts || []);
        setShiftsPag(res?.data?.pagination || { total: 0, pages: 1, page: 1 });
      }
    } catch (e) { console.error(e); }
    finally { mountedRef.current && setShiftsLoading(false); }
  }, [org, period.from_date, period.to_date]);

  // ── Confirm actions ────────────────────────────────────────────────────────

  // queryId = membership_id for the /operations/members/:id/* actions — it was
  // previously dropped, so supervisor / space / location assignment never saved.
  const doAction = async (type, payload, onSuccess, queryId) => {
    setActionLoading(true);
    try {
      await dispatch(actions.addData(type, payload, queryId));
      if (onSuccess) onSuccess(); // addData re-throws on failure, so this only runs on success
      await loadAll();
    } catch (e) { console.error(e); }
    finally { setActionLoading(false); }
  };

  // ── Management modal handlers ──────────────────────────────────────────────

  async function handleToggleSupervisor() {
    await doAction('toggle_supervisor', {
      organization_id: org.organization_id,
      is_supervisor: supervisorForm.is_supervisor,
      managed_location_ids: supervisorForm.managed_location_ids,
    }, () => setShowSupervisor(false), id);
  }

  async function handleAssignSpaces() {
    await doAction('assign_staff_spaces', {
      organization_id: org.organization_id,
      primary_space_id: spaceForm.primary_space_id,
      additional_space_ids: spaceForm.additional_space_ids,
      space_name: spaceForm.space_name,
    }, () => setShowSpace(false), id);
  }

  async function handleAssignLocation() {
    await doAction('assign_resident_location', {
      organization_id: org.organization_id,
      location_id: locationForm.location_id,
      space_id: locationForm.space_id,
      space_name: locationForm.space_name,
      unit_number: locationForm.unit_number,
    }, () => setShowLocation(false), id);
  }

  // Note: addData with membership_id needs the id passed as 3rd arg (orgId slot)
  // Wrap each to pass id correctly:
  // const dispatchWithId = async (type, payload) => {
  //   setActionLoading(true);
  //   try {
  //     await dispatch(actions.addData(type, payload, id));
  //     await loadAll();
  //   } catch (e) { console.error(e); }
  //   finally { setActionLoading(false); }
  // };


  const dispatchWithId = async (type, form, onSuccess) => {
    setActionLoading(true);

    // Construct the unified payload
    const payload = {
      // Identity & Meta
      organization_id: org?.organization_id,
      
      // Role & Career
      // role: form.role,
      position: form.position, 
      is_supervisor: form.is_supervisor,

      // Space Logic
      primary_space_id: form.primary_space_id,
      additional_space_ids: form.additional_space_ids || [],
      
      // Location Logic
      primary_location_id: form.primary_location_id,
      additional_location_ids: form.additional_location_ids || [],
      
      // Resident Specifics
      unit_number: form.unit_number,

      // membership_id: id,
    };

    // console.log(`[MemberDetails] Dispatching Unified Update:`, payload, id);

    try {
      // Using 'update_member' as the type to match your Redux setup
      await dispatch(actions.addData('update_member_details', payload, id)); 
      
      // Refresh the profile data
      await loadAll();
      
      if (onSuccess) onSuccess();
    } catch (e) {
      console.error(`[MemberDetails] Update failed:`, e);
    } finally {
      setActionLoading(false);
    }
  };

  // ── Render guards ──────────────────────────────────────────────────────────

  if (loading) return <MemberDetailsSkeleton />;
  if (!memberData) return (
    <div className="staff-member-details-page">
      <div className="smd-empty">
        <User size={48} />
        <h4>Member not found</h4>
        <p>This member may have been removed or doesn't exist.</p>
        <button className="smd-action-btn smd-action-btn--secondary" style={{ marginTop: 12 }} onClick={() => navigate('/members')}>
          <ArrowLeft size={14} /> Back to Members
        </button>
      </div>
    </div>
  );

  const { user } = memberData;
  const isStaff    = isBusinessMode; // || ['staff', 'security', 'admin', 'manager'].includes(memberData.role);
  const isResident = !isBusinessMode; // || memberData.member_type === 'user';
  const statusKey  = memberData.status || 'pending';
  // alert(memberData.role);
  const canBeSuper = ['admin', 'manager', 'security', 'staff'].includes(memberData.role);

  const statsDef = [
    { icon: <Clock3 size={18} />, bg: '#EFF6FF', color: '#2563EB', label: 'Total Hours', value: statsLoading ? '—' : `${staffStats?.total_hours ?? 0}h`, sub: period.label, subCls: '' },
    { icon: <CheckCircle size={18} />, bg: '#F0FDF4', color: '#16a34a', label: 'On-Time Rate', value: statsLoading ? '—' : `${staffStats?.on_time_percentage ?? 0}%`, sub: staffStats?.performance_rating || '—', subCls: '--success' },
    { icon: <AlertCircle size={18} />, bg: '#FFFBEB', color: '#D97706', label: 'Late Arrivals', value: statsLoading ? '—' : (staffStats?.late_arrivals ?? 0), sub: `${staffStats?.avg_minutes_late ?? 0}m avg`, subCls: '--warning' },
    { icon: <Coffee size={18} />, bg: '#FFF1F2', color: '#F43F5E', label: 'Avg Break', value: statsLoading ? '—' : `${staffStats?.avg_break_minutes ?? 0}m`, sub: 'Per shift', subCls: '' },
  ];

  return (
    <>
      <div className="staff-member-details-page">

        {/* ── Header ── */}
        <div className="smd-header">
          <button className="smd-back" onClick={() => navigate(-1)}><ArrowLeft size={16} /></button>
          <div className="smd-header__text">
            <div className="smd-breadcrumb">
              <span>Members</span><span className="sep">/</span>
              <span className="active">{user?.first_name} {user?.last_name}</span>
            </div>
            <h1>Member Profile</h1>
          </div>
          {isStaff && (
            <div className="smd-header__period">
              <PeriodNav period={period} onPrev={() => setMonthOffset(o => o - 1)} onNext={() => setMonthOffset(o => Math.min(o + 1, 0))} />
            </div>
          )}
        </div>

        {/* ── Stats Bar (staff only) ── */}
        {isStaff && (
          <div className="smd-stats-bar">
            {statsDef.map((s, i) => (
              <div key={i} className="smd-stat">
                <div className="smd-stat__icon" style={{ background: s.bg, color: s.color }}>{s.icon}</div>
                <div>
                  <div className="smd-stat__label">{s.label}</div>
                  <div className={`smd-stat__value ${statsLoading ? 'smd-stat__value--loading' : ''}`}>{s.value}</div>
                  <div className={`smd-stat__sub smd-stat__sub${s.subCls}`}>{s.sub}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Main ── */}
        <div className="smd-main">

          {/* ── Profile sidebar ── */}
          <div className="smd-profile-col">
            <div className="smd-profile-card">
              <div className="smd-profile-hero">
                <div className="smd-avatar-wrap">
                  <div className="smd-avatar">
                  {user?.avatar ? (
                    <img src={user.avatar} alt="" />
                  ) : (
                    <span>
                      {user?.first_name?.[0]}
                      {user?.last_name?.[0]}
                    </span>
                  )}
                </div>
                  <span className={`smd-status-dot smd-status-dot--${statusKey}`} />
                </div>
                <div className="smd-profile-name">{user?.first_name} {user?.last_name}</div>
                <div className="smd-profile-badges">
                  <span className="smd-role-pill">{memberData.role}</span>
                  <span className={`smd-status-pill smd-status-pill--${statusKey}`}>{statusKey}</span>
                  {memberData.is_supervisor && <span className="smd-supervisor-pill">Supervisor</span>}
                </div>
              </div>

              {/* Info chips */}
              <div className="smd-info-chips">
                {memberData.employee_id && (
                  <div className="smd-chip"><div className="smd-chip__label">Employee ID</div><div className="smd-chip__value">{memberData.employee_id}</div></div>
                )}
                {isResident && memberData.unit_number && (
                  <div className="smd-chip"><div className="smd-chip__label">Unit</div><div className="smd-chip__value">{memberData.unit_number}</div></div>
                )}
                {memberData.space_name && (
                  <div className="smd-chip">
                    <div className="smd-chip__label">{isResident ? 'Block / Building' : 'Department'}</div>
                    <div className="smd-chip__value">{memberData.space_name}</div>
                  </div>
                )}
                {memberData.location_id && (
                  <div className="smd-chip">
                    <div className="smd-chip__label">Location</div>
                    <div className="smd-chip__value">{memberData.primary_location_name}</div>
                  </div>
                )}
                <div className="smd-chip">
                  <div className="smd-chip__label">Joined</div>
                  <div className="smd-chip__value">{Moment(memberData.joined_at).format('MMM DD, YYYY')}</div>
                </div>
                <div className="smd-chip">
                  <div className="smd-chip__label">Member For</div>
                  <div className="smd-chip__value">{Moment(memberData.joined_at).fromNow(true)}</div>
                </div>
                {isStaff && !isResident && staffStats?.attendance_score != null && (
                  <div className="smd-chip smd-chip--score" style={{ gridColumn: '1 / -1' }}>
                    <div className="smd-chip__label">Attendance · {period.label}</div>
                    <div className="smd-attendance-score-bar">
                      <div
                        className={`smd-attendance-score-fill ${staffStats.attendance_score >= 75 ? 'smd-attendance-score-fill--good' : staffStats.attendance_score >= 50 ? 'smd-attendance-score-fill--warn' : 'smd-attendance-score-fill--poor'}`}
                        style={{ width: `${staffStats.attendance_score}%` }}
                      />
                    </div>
                    <div className="smd-chip__value">{staffStats.attendance_score}/100 · {staffStats.performance_rating}</div>
                  </div>
                )}
              </div>

              {/* Contact */}
              {(user?.email || user?.phone_number) && (
                <div className="smd-contact">
                  <div className="smd-contact__title">Contact</div>
                  {user.email       && <div className="smd-contact__item"><Mail size={13} />{user.email}</div>}
                  {user.phone_number && <div className="smd-contact__item"><Phone size={13} />{user.phone_number}</div>}
                </div>
              )}

              {/* Actions */}
              <div className="smd-actions">
                {memberData.status === 'active'
                  ? <button className="smd-action-btn smd-action-btn--warning" onClick={() => setShowSuspend(true)}><UserX size={15} /> Suspend</button>
                  : <button className="smd-action-btn smd-action-btn--success" onClick={() => setShowReactivate(true)}><UserCheck size={15} /> Reactivate</button>
                }

               
                <button className="smd-action-btn smd-action-btn--info" onClick={() => setShowManageModal(true)}>
                  <Settings2 size={16} /> Manage Member
                </button>

                <button className="smd-action-btn smd-action-btn--secondary" onClick={() => { setRoleValue(memberData.role || 'member'); setShowRole(true); }}>
                  <Shield size={15} /> Change Role
                </button>



                <button className="smd-action-btn smd-action-btn--danger" onClick={() => setShowDelete(true)}>
                  <Trash2 size={15} /> Remove Member
                </button>
              </div>
            </div>
          </div>

          {/* ── Detail tabs ── */}
          <div className="smd-details-col">
            <div className="smd-details-card">
              <Tabs activeKey={activeTab} onSelect={setActiveTab} className="smd-tabs">

                <Tab eventKey="overview" title={<TabLabel icon={Activity} label="Overview" />}>
                  <div className="smd-tab-body">

                    {/* Performance — staff only */}
                    {isStaff && !isResident && (
                      <div className="smd-section">
                        <div className="smd-section__head"><TrendingUp size={16} /><h3>Performance · {period.label}</h3></div>
                        <div className="smd-perf-grid">
                          {[
                            { icon: <CheckCircle size={18} />, bg: '#F0FDF4', color: '#16a34a', value: staffStats?.shifts_count ?? '—', label: 'Total Shifts' },
                            { icon: <Timer size={18} />, bg: '#EFF6FF', color: '#2563EB', value: staffStats?.overtime_hours != null ? `${staffStats.overtime_hours}h` : '—', label: 'Overtime' },
                            { icon: <Coffee size={18} />, bg: '#FFFBEB', color: '#D97706', value: staffStats?.total_breaks ?? '—', label: 'Total Breaks' },
                            { icon: <Award size={18} />, bg: '#F5F3FF', color: '#7C3AED', value: staffStats?.attendance_score != null ? `${staffStats.attendance_score}` : '—', label: 'Attendance Score' },
                          ].map((c, i) => (
                            <div key={i} className="smd-perf-card">
                              <div className="smd-perf-card__icon" style={{ background: c.bg, color: c.color }}>{c.icon}</div>
                              <div><div className="smd-perf-card__value">{statsLoading ? '—' : c.value}</div><div className="smd-perf-card__label">{c.label}</div></div>
                            </div>
                          ))}
                        </div>
                        {!statsLoading && staffStats && (staffStats.late_arrivals > 0 || staffStats.early_arrivals > 0) && (
                          <div className="smd-lateness-row">
                            {staffStats.late_arrivals > 0 && <div className="smd-lateness-chip smd-lateness-chip--late"><AlertCircle size={12} />{staffStats.late_arrivals} late{staffStats.avg_minutes_late > 0 && ` · avg ${staffStats.avg_minutes_late}m`}</div>}
                            {staffStats.early_arrivals > 0 && <div className="smd-lateness-chip smd-lateness-chip--early"><Clock size={12} />{staffStats.early_arrivals} early</div>}
                            {staffStats.completion_rate != null && <div className="smd-lateness-chip"><CheckCircle size={12} />{staffStats.completion_rate}% completion</div>}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Work / Location details */}
                    <div className="smd-section">
                      <div className="smd-section__head"><Briefcase size={16} /><h3>{isResident ? 'Location Details' : 'Work Details'}</h3></div>
                      <div className="smd-detail-grid">
                        {isResident ? (
                          <>
                            {memberData.space_name && <div className="smd-detail-box"><div className="smd-detail-box__label">Block / Building</div><div className="smd-detail-box__value">{memberData.space_name}</div></div>}
                            {memberData.unit_number && <div className="smd-detail-box"><div className="smd-detail-box__label">Unit Number</div><div className="smd-detail-box__value">{memberData.unit_number}</div></div>}
                            {memberData.location_id && <div className="smd-detail-box"><div className="smd-detail-box__label">Location ID</div><div className="smd-detail-box__value">{memberData.location_id}</div></div>}
                          </>
                        ) : (
                          <>
                            {memberData.space_name && <div className="smd-detail-box"><div className="smd-detail-box__label">Department</div><div className="smd-detail-box__value">{memberData.space_name}</div></div>}
                            {memberData.additional_space_ids?.length > 0 && <div className="smd-detail-box"><div className="smd-detail-box__label">Also Covers</div><div className="smd-detail-box__value">{memberData.additional_space_ids.length} unit{memberData.additional_space_ids.length > 1 ? 's' : ''}</div></div>}
                            {
                            memberData.staff_role && 
                            <div className="smd-detail-box"><div className="smd-detail-box__label">Staff Role</div><div className="smd-detail-box__value" style={{ textTransform: 'capitalize' }}>{memberData.staff_role}</div></div>}
                            {memberData.is_supervisor && <div className="smd-detail-box"><div className="smd-detail-box__label">Supervisor Scope</div><div className="smd-detail-box__value">{memberData.managed_location_ids?.length ? `${memberData.managed_location_ids.length} location(s)` : 'Org-wide'}</div></div>}
                          </>
                        )}
                        <div className="smd-detail-box">
                          <div className="smd-detail-box__label">Last Active</div>
                          <div className="smd-detail-box__value">{user?.last_login ? Moment(user.last_login).fromNow() : 'Never'}</div>
                        </div>
                        <div className="smd-detail-box">
                          <div className="smd-detail-box__label">Account Status</div>
                          <div className="smd-detail-box__value">
                            <span className={`smd-status-pill smd-status-pill--${user?.is_active ? 'active' : 'suspended'}`}>{user?.is_active ? 'Active' : 'Inactive'}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Role */}
                    <div className="smd-section">
                      <div className="smd-section__head"><Shield size={16} /><h3>Role & Access</h3></div>
                      <div className="smd-role-block">
                        <div className="smd-role-badge">{memberData.role?.toUpperCase()}</div>
                        {memberData.is_supervisor && <div className="smd-role-badge" style={{ background: '#D97706', marginLeft: 6 }}>SUPERVISOR</div>}
                        <p>{getRoleDescription(memberData.role)}</p>
                      </div>
                    </div>
                  </div>
                </Tab>

                {/* Attendance tab — staff only */}
                {isStaff && !isResident && (
                  <Tab eventKey="attendance" title={<TabLabel icon={Clock} label="Attendance" count={shiftsPag.total > 0 ? shiftsPag.total : undefined} />}>
                    <div className="smd-tab-body">
                      <div className="smd-section">
                        <div className="smd-section__head">
                          <BarChart3 size={16} />
                          <h3>Shifts · {period.label}</h3>
                          {shiftsPag.total > 0 && <span className="smd-section__count">{shiftsPag.total} total</span>}
                        </div>
                        <AttendanceSummary shifts={shifts} />
                        {shiftsLoading ? (
                          <div className="smd-shifts-loading">{[1,2,3].map(i => <div key={i} className="smd-shift-skeleton" />)}</div>
                        ) : shifts.length > 0 ? (
                          <>
                            <div className="smd-shifts">{shifts.map((shift, i) => <ShiftRow key={shift.log_id || i} shift={shift} />)}</div>
                            {shiftsPag.pages > 1 && (
                              <div className="smd-pagination">
                                <button className="smd-page-btn" disabled={shiftsPag.page <= 1} onClick={() => fetchShifts(memberData.user_id, shiftsPag.page - 1)}><ChevronLeft size={14} /> Prev</button>
                                <span className="smd-page-info">Page {shiftsPag.page} of {shiftsPag.pages}</span>
                                <button className="smd-page-btn" disabled={shiftsPag.page >= shiftsPag.pages} onClick={() => fetchShifts(memberData.user_id, shiftsPag.page + 1)}>Next <ChevronRight size={14} /></button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="smd-empty-state">
                            <Clock size={40} />
                            <h4>No Shifts in {period.label}</h4>
                            <p>No attendance records for this period.</p>
                            {monthOffset < 0 && <button className="smd-period-jump" onClick={() => setMonthOffset(0)}>Back to current month</button>}
                          </div>
                        )}
                      </div>
                    </div>
                  </Tab>
                )}

              </Tabs>
            </div>
          </div>
        </div>
      </div>

      {/* ── Confirm Modals ── */}
      <ConfirmActionModal show={showSuspend} onHide={() => setShowSuspend(false)} onConfirm={() => doAction('suspend_member', { membership_id: id }, () => setShowSuspend(false))}
        title="Suspend Member" message={<div><p>Suspend <strong>{user?.first_name} {user?.last_name}</strong>?</p><p className="mt-2">They will lose access but can be reactivated later.</p></div>}
        confirmText="Suspend" type="warning" loading={actionLoading} />
      <ConfirmActionModal show={showReactivate} onHide={() => setShowReactivate(false)} onConfirm={() => doAction('reactivate_member', { membership_id: id }, () => setShowReactivate(false))}
        title="Reactivate Member" message={<div><p>Reactivate <strong>{user?.first_name} {user?.last_name}</strong>?</p></div>}
        confirmText="Reactivate" type="success" loading={actionLoading} />
      <ConfirmActionModal show={showDelete} onHide={() => setShowDelete(false)} onConfirm={() => doAction('delete_member', { membership_id: id }, () => navigate('/members'))}
        title="Remove Member" message={<div><p>Permanently remove <strong>{user?.first_name} {user?.last_name}</strong>?</p><p className="mt-2 text-danger"><strong>This cannot be undone.</strong></p></div>}
        confirmText="Remove Permanently" type="danger" loading={actionLoading} />

      {/* ── Supervisor Modal ── */}
      <Modal show={showSupervisor} onClose={() => setShowSupervisor(false)} title="Supervisor Access" icon={Shield}
        footer={
          <>
               <button className="smd-action-btn smd-action-btn--ghost" onClick={() => setShowSupervisor(false)}>
                Cancel
              </button>
              <button
                className="smd-action-btn smd-action-btn--primary"
                disabled={actionLoading}
                onClick={() =>
                  dispatchWithId(
                    'toggle_supervisor',
                    {
                      organization_id:      org.organization_id,
                      is_supervisor:        supervisorForm.is_supervisor,
                      managed_location_ids: supervisorForm.managed_location_ids,
                    },
                    () => setShowSupervisor(false)
                  )
                }
              >
                {actionLoading ? 'Saving…' : 'Save Changes'}
              </button>
          </>
        }
      >
        <p className="smd-modal-desc">Supervisors can clock out staff, correct shifts, and change roles within their managed locations.</p>
        <label className="smd-toggle-row">
          <span>Enable supervisor access</span>
          <input type="checkbox" checked={supervisorForm.is_supervisor} onChange={e => setSupervisorForm(f => ({ ...f, is_supervisor: e.target.checked }))} style={{ accentColor: '#5341C4', width: 18, height: 18, cursor: 'pointer' }} />
        </label>
        {supervisorForm.is_supervisor && (
          <div style={{ marginTop: 16 }}>
            <div className="ops-config-label">Managed Locations</div>
            <p style={{ fontSize: 12, color: '#7E8299', marginBottom: 10 }}>Leave empty for org-wide access.</p>
            {spaces.length === 0 ? <p style={{ fontSize: 12, color: '#9CA3AF' }}>No spaces found — will apply org-wide.</p> : spaces.map(s => (
              <label key={s.space_id} className="smd-check-row">
                <input type="checkbox" style={{ accentColor: '#5341C4' }}
                  checked={supervisorForm.managed_location_ids.includes(s.space_id)}
                  onChange={e => { const ids = e.target.checked ? [...supervisorForm.managed_location_ids, s.space_id] : supervisorForm.managed_location_ids.filter(id => id !== s.space_id); setSupervisorForm(f => ({ ...f, managed_location_ids: ids })); }}
                />
                {s.name}
              </label>
            ))}
          </div>
        )}
      </Modal>

  <Modal
        show={showManageModal}
        onClose={() => setShowManageModal(false)}
        title={`Edit ${isResident ? 'Resident' : 'Staff'} Profile`}
        icon={Settings2}
        footer={
          <>
            {/* <button className="smd-action-btn smd-action-btn--ghost" onClick={() => setShowManageModal(false)}>
              Cancel
            </button> */}
            <button
              className="btn btn-primary btn-sm"
              disabled={actionLoading}
              onClick={() => {
                const finalForm = {
                  ...manageForm,
                  position: manageForm.position === 'Other' && customRole.trim()
                    ? customRole.trim()
                    : manageForm.position,
                };
                dispatchWithId('update_membership_details', finalForm, () => setShowManageModal(false));
              }}
            >
              {actionLoading ? 'Saving…' : 'Save Changes'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
 
          {/* ── STAFF: Position + Primary department ── */}
          {!isResident && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
 
              <div>
                <div className="ops-config-label">Position / Title</div>
                <select
                  className="ops-select"
                  style={{ width: '100%' }}
                  value={manageForm.position}
                  onChange={e => {
                    setManageForm({ ...manageForm, position: e.target.value });
                    if (e.target.value !== 'Other') setCustomRole('');
                  }}
                >
                  <option value="">Select position</option>
                  {STAFF_ROLES.map(group => (
                    <optgroup key={group.group} label={group.group}>
                      {group.options.map(o => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {/* Custom role input — only shown when "Other" is selected */}
                {manageForm.position === 'Other' && (
                  <input
                    className="ops-input"
                    style={{ marginTop: 8 }}
                    placeholder="Enter custom role…"
                    value={customRole}
                    onChange={e => setCustomRole(e.target.value)}
                    autoFocus
                  />
                )}
              </div>
 
              <div>
                <div className="ops-config-label">Primary Department</div>
                <select
                  className="ops-select"
                  style={{ width: '100%' }}
                  value={manageForm.primary_space_id}
                  onChange={e => setManageForm({ ...manageForm, primary_space_id: e.target.value })}
                >
                  <option value="">Select department</option>
                  {spaces.map(s => (
                    <option key={s.space_id || s.id} value={s.space_id || s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
 
          {/* ── RESIDENT: primary space only ── */}
          {isResident && (
            <div>
              <div className="ops-config-label">Assigned Block / Building</div>
              <select
                className="ops-select"
                style={{ width: '100%' }}
                value={manageForm.primary_space_id}
                onChange={e => setManageForm({ ...manageForm, primary_space_id: e.target.value })}
              >
                <option value="">Select unit</option>
                {spaces.map(s => (
                  <option key={s.space_id || s.id} value={s.space_id || s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}
 
          {/* ── STAFF: additional departments ── */}
          {!isResident && spaces.length > 1 && (
            <div>
              <div className="ops-config-label">Additional {getLabel('space_name')}</div>
              <div style={{ maxHeight: 110, overflowY: 'auto', border: '1px solid #EAEAF0', borderRadius: 8, padding: '6px 10px' }}>
                {spaces
                  .filter(s => (s.space_id || s.id) !== manageForm.primary_space_id)
                  .map(s => (
                    <label key={s.space_id || s.id} className="smd-check-row">
                      <input
                        type="checkbox"
                        style={{ accentColor: '#5341C4' }}
                        checked={manageForm.additional_space_ids.includes(s.space_id || s.id)}
                        onChange={e => {
                          const id  = s.space_id || s.id;
                          const ids = e.target.checked
                            ? [...manageForm.additional_space_ids, id]
                            : manageForm.additional_space_ids.filter(x => x !== id);
                          setManageForm({ ...manageForm, additional_space_ids: ids });
                        }}
                      />
                      <span style={{ fontSize: 12 }}>{s.name}</span>
                    </label>
                  ))}
              </div>
            </div>
          )}
 
          {/* ── Locations (primary + additional) ── */}
          {org?.locations?.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: org?.locations?.length > 1 ? '1fr 1fr' : '1fr', gap: 12 }}>
 
              <div>
                <div className="ops-config-label">Primary Location</div>
                <select
                  className="ops-select"
                  style={{ width: '100%' }}
                  value={manageForm.primary_location_id}
                  onChange={e => setManageForm({ ...manageForm, primary_location_id: e.target.value })}
                >
                  <option value="">Select location</option>
                  {(org.locations || []).map(l => (
                    <option key={l.location_id} value={l.location_id}>
                      {l.name}{l.is_primary ? ' (Primary)' : ''}
                    </option>
                  ))}
                </select>
              </div>
 
              {/* Additional locations — only when org has more than 1 */}
              {org.locations.length > 1 && (
                <div>
                  <div className="ops-config-label">Additional Locations</div>
                  <div style={{ maxHeight: 100, overflowY: 'auto', border: '1px solid #EAEAF0', borderRadius: 8, padding: '6px 10px' }}>
                    {(org.locations || [])
                      .filter(l => l.location_id !== manageForm.primary_location_id)
                      .map(l => (
                        <label key={l.location_id} className="smd-check-row">
                          <input
                            type="checkbox"
                            style={{ accentColor: '#5341C4' }}
                            checked={(manageForm.additional_location_ids || []).includes(l.location_id)}
                            onChange={e => {
                              const ids = e.target.checked
                                ? [...(manageForm.additional_location_ids || []), l.location_id]
                                : (manageForm.additional_location_ids || []).filter(x => x !== l.location_id);
                              setManageForm({ ...manageForm, additional_location_ids: ids });
                            }}
                          />
                          <span style={{ fontSize: 12 }}>
                            {l.name}{l.is_primary ? <> &nbsp;<span style={{ color: '#D97706', fontSize: 10, fontWeight: 700 }}>PRIMARY</span></> : ''}
                          </span>
                        </label>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
 
          {/* ── STAFF: supervisor toggle ── */}
          {!isResident && (
            <label
              className="smd-check-row"
              style={{ padding: '11px 14px', background: '#F9F9FF', borderRadius: 8, border: '1px solid #EDEAFC', marginTop: 2, cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                style={{ accentColor: '#5341C4', marginTop: 1 }}
                checked={manageForm.is_supervisor}
                onChange={e => setManageForm({ ...manageForm, is_supervisor: e.target.checked })}
              />
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#3D2FA0', lineHeight: 1.3 }}>Set as Supervisor</div>
                <div style={{ fontSize: 11, color: '#7E8299', marginTop: 2 }}>
                  Can manage other staff, correct shifts and view access logs.
                </div>
              </div>
            </label>
          )}
 
        </div>
      </Modal>

      {/* ── Space Assignment Modal (staff) ── */}
      <Modal show={showSpace} onClose={() => setShowSpace(false)} title="Assign Department / Space" icon={MapPin}
        footer={
          <>
             <button className="smd-action-btn smd-action-btn--ghost" onClick={() => setShowSpace(false)}>
              Cancel
            </button>
            <button
              className="smd-action-btn smd-action-btn--primary"
              disabled={!spaceForm.primary_space_id || actionLoading}
              onClick={() =>
                dispatchWithId(
                  'assign_staff_spaces',
                  {
                    organization_id:      org.organization_id,
                    primary_space_id:     spaceForm.primary_space_id,
                    additional_space_ids: spaceForm.additional_space_ids,
                    space_name:           spaceForm.space_name,
                  },
                  () => setShowSpace(false)
                )
              }
            >
              {actionLoading ? 'Saving…' : 'Assign'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div className="ops-config-label">Primary Department (default for clock-in)</div>
            <select className="ops-select" style={{ width: '100%' }} value={spaceForm.primary_space_id}
              onChange={e => { const s = spaces.find(x => x.space_id === e.target.value); setSpaceForm(f => ({ ...f, primary_space_id: e.target.value, space_name: s?.name || f.space_name })); }}>
              <option value="">Select primary space</option>
              {spaces.map(s => <option key={s.space_id} value={s.space_id}>{s.name}</option>)}
            </select>
          </div>
          {spaces.filter(s => s.space_id !== spaceForm.primary_space_id).length > 0 && (
            <div>
              <div className="ops-config-label">Additional Spaces (optional)</div>
              {spaces.filter(s => s.space_id !== spaceForm.primary_space_id).map(s => (
                <label key={s.space_id} className="smd-check-row">
                  <input type="checkbox" style={{ accentColor: '#5341C4' }}
                    checked={spaceForm.additional_space_ids.includes(s.space_id)}
                    onChange={e => { const ids = e.target.checked ? [...spaceForm.additional_space_ids, s.space_id] : spaceForm.additional_space_ids.filter(id => id !== s.space_id); setSpaceForm(f => ({ ...f, additional_space_ids: ids })); }}
                  />
                  {s.name}
                </label>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* ── Location Modal (residents) ── */}
      <Modal show={showRole} onClose={() => setShowRole(false)} title="Change Role" icon={Shield}
        footer={
          <>
            <button className="smd-action-btn smd-action-btn--ghost" onClick={() => setShowRole(false)}>Cancel</button>
            <button
              className="smd-action-btn smd-action-btn--primary"
              disabled={actionLoading || !roleValue || roleValue === memberData?.role}
              onClick={() => doAction('update_member_role', { organization_id: org.organization_id, new_role: roleValue }, () => setShowRole(false), id)}
            >
              {actionLoading ? 'Saving…' : 'Save role'}
            </button>
          </>
        }>
        <div style={{ display: 'grid', gap: 8 }}>
          {MEMBER_ROLES.map(r => (
            <label key={r.value} style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
              border: `1px solid ${roleValue === r.value ? '#5341C4' : '#EAEAF0'}`, background: roleValue === r.value ? '#F0EEFF' : '#fff',
            }}>
              <input type="radio" name="member_role" value={r.value} checked={roleValue === r.value} onChange={() => setRoleValue(r.value)} style={{ marginTop: 3, accentColor: '#5341C4' }} />
              <span>
                <strong style={{ display: 'block', fontSize: 13 }}>{r.label}</strong>
                <span style={{ fontSize: 12, color: '#7E8299' }}>{r.hint}</span>
              </span>
            </label>
          ))}
          <p style={{ fontSize: 12, color: '#7E8299', margin: '4px 0 0' }}>Only admins can make someone an admin. The change applies the next time they sign in.</p>
        </div>
      </Modal>

      <Modal show={showLocation} onClose={() => setShowLocation(false)} title="Set Resident Location" icon={MapPin}
        footer={
          <>
            <button className="smd-action-btn smd-action-btn--ghost" onClick={() => setShowLocation(false)}>
              Cancel
            </button>
            <button
              className="smd-action-btn smd-action-btn--primary"
              disabled={!locationForm.space_id || actionLoading}
              onClick={() =>
                dispatchWithId(
                  'assign_resident_location',
                  {
                    organization_id: org.organization_id,
                    location_id:     locationForm.location_id,
                    space_id:        locationForm.space_id,
                    space_name:      locationForm.space_name,
                    unit_number:     locationForm.unit_number,
                  },
                  () => setShowLocation(false)
                )
              }
            >
              {actionLoading ? 'Saving…' : 'Save Location'}
            </button>
          </>
        }
      >
       <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
  
  {org?.locations?.length > 1 && (
    <div>
      <div className="ops-config-label">Physical Location / Branch</div>
      <select
        className="ops-select"
        style={{ width: '100%' }}
        value={locationForm.location_id}
        onChange={(e) => setLocationForm((f) => ({ ...f, location_id: e.target.value }))}
      >
        <option value="">Select location (optional)</option>
        {(org.locations || []).map((l) => (
          <option key={l.location_id} value={l.location_id}>
            {l.name}{l.is_primary ? ' (Primary)' : ''}
          </option>
        ))}
      </select>
    </div>
  )}
 
  <div>
    <div className="ops-config-label">Block / Building</div>
    <select
      className="ops-select"
      style={{ width: '100%' }}
      value={locationForm.space_id}
      onChange={(e) => {
        const s = spaces.find((x) => x.space_id === e.target.value);
        setLocationForm((f) => ({ ...f, space_id: e.target.value, space_name: s?.name || '' }));
      }}
    >
      <option value="">Select block / building</option>
      {spaces.map((s) => (
        <option key={s.space_id} value={s.space_id}>{s.name}</option>
      ))}
    </select>
  </div>
 
  <div>
    <div className="ops-config-label">Unit / House Number</div>
    <input
      className="ops-input"
      placeholder="e.g. A12, Flat 3B, House 7"
      value={locationForm.unit_number}
      onChange={(e) => setLocationForm((f) => ({ ...f, unit_number: e.target.value }))}
    />
  </div>
        </div>
      </Modal>
    </>
  );
}

function getRoleDescription(role) {
  const map = {
    admin:    'Full access to all organization features and settings.',
    manager:  'Can manage staff, access codes, view logs and analytics.',
    security: 'Code validation, emergency response, and gate monitoring.',
    staff:    'Clock in/out, break management, and basic code validation.',
    member:   'Basic access to validate codes and view logs.',
  };
  return map[role] || 'Standard member access.';
}